import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import type { GoalDirection, GoalMetric } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Select } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { createGoal } from '../lib/gymApi';
import { GOAL_TEMPLATES, METRICS, cadenceLabel, type GoalTemplate } from '../lib/goals';

export function GoalFormPage() {
  const { id: memberId = '' } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState<GoalMetric>('WEIGHT');
  const [unit, setUnit] = useState('kg');
  const [direction, setDirection] = useState<GoalDirection>('DOWN');
  const [startValue, setStartValue] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [cadenceDays, setCadenceDays] = useState('14');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function applyTemplate(t: GoalTemplate) {
    setTitle(t.title);
    setMetric(t.metric);
    setUnit(t.unit);
    setDirection(t.direction);
    setCadenceDays(String(t.cadenceDays));
  }

  function onMetricChange(m: GoalMetric) {
    setMetric(m);
    const def = METRICS.find((x) => x.value === m);
    if (def?.unit) setUnit(def.unit);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) return setError('Give the goal a name.');
    if (!unit.trim()) return setError('Set a unit (e.g. kg, cm, %).');
    const cadence = parseInt(cadenceDays, 10);
    if (!(cadence >= 1)) return setError('Check-in frequency must be at least 1 day.');
    setSubmitting(true);
    try {
      const res = await createGoal(memberId, {
        title: title.trim(),
        metric,
        unit: unit.trim(),
        direction,
        startValue: startValue === '' ? null : Number(startValue),
        targetValue: targetValue === '' ? null : Number(targetValue),
        cadenceDays: cadence,
      });
      navigate(`/goals/${res.goal.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not create the goal.');
      setSubmitting(false);
    }
  }

  return (
    <section className="animate-fade-up">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Back
      </button>

      <PageHeader eyebrow="New goal" title="Add a goal" description="Start from a common goal or build your own. Set a target and we’ll close it automatically when it’s reached." />

      <form onSubmit={onSubmit}>
        <Card className="flex flex-col gap-5 p-5 md:p-6">
          <div>
            <p className="text-sm font-medium text-ink">Start from a template</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {GOAL_TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  className="rounded-pill bg-surface px-3 py-1.5 text-sm font-medium text-ink-soft transition-colors hover:bg-line-strong"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <Input label="Goal name" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Lose weight to 70kg" autoFocus />

          <div className="grid gap-5 sm:grid-cols-2">
            <Select label="What we measure" value={metric} onChange={(e) => onMetricChange(e.target.value as GoalMetric)}>
              {METRICS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
            <Input label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="kg" />
          </div>

          <Select label="Direction" value={direction} onChange={(e) => setDirection(e.target.value as GoalDirection)}>
            <option value="DOWN">Lower is better (lose / reduce)</option>
            <option value="UP">Higher is better (gain / increase)</option>
          </Select>

          <div className="grid gap-5 sm:grid-cols-2">
            <Input label="Starting value (optional)" inputMode="decimal" value={startValue} onChange={(e) => setStartValue(e.target.value)} placeholder="e.g. 78" />
            <Input label="Target (optional)" inputMode="decimal" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} placeholder="e.g. 70" hint="Leave blank to just track the trend." />
          </div>

          <Input
            label="Check in every (days)"
            inputMode="numeric"
            value={cadenceDays}
            onChange={(e) => setCadenceDays(e.target.value)}
            hint={Number(cadenceDays) >= 1 ? cadenceLabel(Number(cadenceDays)) : undefined}
          />

          {error && (
            <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2.5 text-sm font-medium text-bad-ink">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {!submitting && <Check className="size-4" aria-hidden />} Create goal
            </Button>
          </div>
        </Card>
      </form>
    </section>
  );
}
